import { Avatar, Box, Grid, Tooltip, Typography } from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import React, { useState } from "react";
import AchievementChip from '../common/AchievementChip';
import CustomPagination from '../common/Pagination';
import AchievementDetailModal from '../modals/AchievementDetailModal';
import {
    ACHIEVEMENTS_PER_PAGE,
    ACHIEVEMENT_CARD_MIN_HEIGHT,
    ACHIEVEMENT_CARD_WIDTH,
    DEFAULT_AVATAR_BACKGROUND,
    DEFAULT_BACKGROUND_COLOR,
    HOVER_BACKGROUND_COLOR,
    HOVER_TRANSITION_DURATION,
    LOCKED_AVATAR_BACKGROUND,
    PET_TYPE
} from '../utils/constants';
import { calculatePagination, formatAchievementDate } from '../utils/helpers';
import AvatarGroupStacked from './AvatarGroupStacked';

const AchievementGrid = ({
    achievements = [],
    getAchievementColors,
    isLocked = false,
    isLoading = false,
    selectedPetId = null,
    patchUserSelectedPet = () => {}
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedAchievement, setSelectedAchievement] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    // Pagination logic
    const { currentItems: currentAchievements, totalPages } = calculatePagination(
        achievements,
        currentPage,
        ACHIEVEMENTS_PER_PAGE
    );

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };

    const handleCardClick = (ach) => {
        setSelectedAchievement(ach);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedAchievement(null);
    };

    const handlePetClick = (e, cosmeticId) => {
        e.stopPropagation();
        const newPetId = cosmeticId === selectedPetId ? null : cosmeticId;
        patchUserSelectedPet(newPetId);
    };

    if (isLoading) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Loading achievements...
            </Typography>
        );
    }

    if (achievements.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No achievements {isLocked ? 'to unlock' : 'unlocked yet'}
            </Typography>
        );
    }

    return (
        <>
            <Grid container spacing={2} justifyContent="center">
                {currentAchievements.map((ach, index) => {
                    const difficultyColors = getAchievementColors ? getAchievementColors(ach.achievement_type) : { bgcolor: '#e0e0e0', color: '#333' };
                    const isPet = ach.reward_type === PET_TYPE;
                    const isSelectedPet = isPet && ach.cosmetic_id === selectedPetId;

                    return (
                        <Grid item key={`${ach.achievement_id || ach.id}-${index}`} xs={12} sm={6} md={4}>
                            <Grid
                                container
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                    background: isLocked ? 'transparent' : DEFAULT_BACKGROUND_COLOR,
                                    borderRadius: 2,
                                    boxShadow: isLocked ? 0 : 1,
                                    border: isLocked ? `1px dashed ${difficultyColors.bgcolor}` : 'none',
                                    width: ACHIEVEMENT_CARD_WIDTH,
                                    minHeight: ACHIEVEMENT_CARD_MIN_HEIGHT,
                                    p: 2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    m: 'auto',
                                    cursor: 'pointer',
                                    transition: HOVER_TRANSITION_DURATION,
                                    '&:hover': {
                                        background: HOVER_BACKGROUND_COLOR,
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0px)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    },
                                }}
                                onClick={() => handleCardClick(ach)}
                            >
                                {/* Badge o status */}
                                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                                    <AchievementChip
                                        achievementType={ach.achievement_type}
                                        getAchievementColors={getAchievementColors}
                                        size="small"
                                        sx={{ mb: 1 }}
                                    />
                                </Box>
                                
                                {/* Icon */}
                                <Grid item>
                                    <Avatar
                                        src={ach.signed_thumbnail_url}
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            bgcolor: isLocked ? LOCKED_AVATAR_BACKGROUND : DEFAULT_AVATAR_BACKGROUND,
                                            borderRadius: 2,
                                            mr: 2,
                                            filter: isLocked ? 'grayscale(100%) opacity(60%)' : 'none',
                                            objectFit: isLocked ? 'cover' : 'none'
                                        }}
                                    />
                                </Grid>

                                {/* Info */}
                                <Grid item xs>
                                    <Typography 
                                        variant="subtitle2" 
                                        fontWeight={600} 
                                        sx={{ 
                                            lineHeight: 1,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {ach.name}
                                    </Typography>
                                    {!isLocked && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {ach.completed_at ? formatAchievementDate(ach.completed_at) : ''}
                                        </Typography>
                                    )}
                                </Grid>

                                {/* Pet logic */}
                                {isPet && !isLocked && (
                                    <Tooltip title="Toggle selected pet" arrow placement="top">
                                        <Box 
                                            sx={{ position: 'absolute', bottom: 8, left: 8, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                            onClick={(e) => handlePetClick(e, ach.cosmetic_id)}
                                        >
                                            <IconComponent 
                                                iconName={isSelectedPet ? 'paw' : 'paw-outline'} 
                                                style={{ fontSize: 18, color: isSelectedPet ? '#1a242d' : 'lightgray' }} 
                                            />
                                        </Box>
                                    </Tooltip>
                                )}

                                {/* Corner decoration for Unlocked achievements */}
                                {!isLocked && (
                                    <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
                                        <AvatarGroupStacked 
                                            usernames={ach.usernames || []} 
                                            users_photo_links={ach.photo_links || []} 
                                            maxAvatars={3} 
                                            useRandomColors={true} 
                                        />
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            )}

            <AchievementDetailModal
                open={modalOpen}
                onClose={handleCloseModal}
                achievement={selectedAchievement}
                getAchievementColors={getAchievementColors}
                isLocked={isLocked}
            />
        </>
    );
};

export default AchievementGrid; 